import React from 'react';
import { Shield } from 'lucide-react';
import Header from './components/Header21';
import Footer from './components/Footer21';

const PrivacyPolicy21 = () => {
    const sections = [
        {
            id: 'information-we-collect',
            title: '1. Information We Collect',
            paragraphs: [
                'We collect personal information that you voluntarily provide to us when you register on our website, place an order, subscribe to our newsletter, or contact our customer support team. This information may include your full name, email address, phone number, shipping and billing addresses, date of birth, and body measurements for custom-sizing purposes.',
                'When you make a purchase, your payment information (such as credit card number, UPI ID, or net banking details) is collected and processed directly by our payment gateway partner, Razorpay. We do not store your complete payment card details on our servers. Razorpay is PCI-DSS compliant and adheres to the highest standards of payment security.',
                'We also automatically collect certain technical information when you visit our website, including your IP address, browser type and version, device type, operating system, referring URL, pages viewed, time spent on pages, and clickstream data. This information is collected through cookies and similar tracking technologies to help us improve your browsing experience.'
            ]
        },
        {
            id: 'how-we-use-information',
            title: '2. How We Use Your Information',
            paragraphs: [
                'We use the information we collect to process and fulfil your orders, manage your account, communicate with you about your purchases and order status, and provide customer support. Your contact details may be used to send transactional notifications such as order confirmations, shipping updates via Shiprocket tracking, and delivery confirmations.',
                'With your explicit consent, we may use your email address and phone number to send you marketing communications about new collections, exclusive offers, seasonal sales, and events. You can opt out of marketing communications at any time by clicking the unsubscribe link in any email or by updating your communication preferences in your account settings.',
                'We use aggregated and anonymised data to analyse shopping trends, improve our product offerings, optimise website performance, personalise your browsing experience, and conduct internal business analytics. This data helps us understand what our customers love and enables us to serve you better.'
            ]
        },
        {
            id: 'data-security',
            title: '3. Data Security',
            paragraphs: [
                'We implement robust technical and organisational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Our website uses SSL/TLS encryption to secure all data transmitted between your browser and our servers. All payment transactions are processed through Razorpay\'s secure, PCI-DSS Level 1 certified infrastructure.',
                'Access to personal data is restricted to authorised personnel only, on a need-to-know basis. We conduct regular security audits and vulnerability assessments of our systems. Our hosting infrastructure employs firewalls, intrusion detection systems, and regular backups to ensure data integrity and availability.',
                'While we take every reasonable precaution to protect your information, no method of electronic transmission or storage is completely secure. We cannot guarantee absolute security, but we are committed to promptly notifying affected users in the event of any data breach, in accordance with applicable Indian data protection regulations.'
            ]
        },
        {
            id: 'cookies-tracking',
            title: '4. Cookies & Tracking',
            paragraphs: [
                'Our website uses cookies and similar tracking technologies to enhance your browsing experience, remember your preferences, and gather analytical data. Essential cookies are required for the basic functioning of our website, including maintaining your shopping cart and processing checkout. These cannot be disabled.',
                'We use Google Analytics to collect anonymised data about website traffic and user behaviour. This helps us understand how visitors interact with our website, which pages are most popular, and where improvements are needed. Google Analytics uses cookies to collect this information, but it does not identify individual users. You can opt out of Google Analytics tracking by installing the Google Analytics Opt-out Browser Add-on.',
                'We may also use cookies from social media platforms (such as Instagram and Facebook) to enable social sharing features and to display targeted advertisements on those platforms. You can manage your cookie preferences through your browser settings. Please note that disabling certain cookies may affect the functionality of our website.'
            ]
        },
        {
            id: 'third-party-services',
            title: '5. Third-Party Services',
            paragraphs: [
                'We work with trusted third-party service providers to operate our business effectively. These partners include: Razorpay for secure payment processing, Shiprocket for order fulfilment and shipping logistics, Google Analytics for website analytics, and cloud hosting providers for data storage and website infrastructure.',
                'Each of these service providers is contractually obligated to protect your personal information and may only use it for the specific purposes for which it was shared. We do not sell, trade, or rent your personal information to any third parties for their marketing purposes.',
                'Our website may contain links to third-party websites, including social media platforms and partner brands. We are not responsible for the privacy practices, content, or security of these external websites. We encourage you to read the privacy policies of any third-party website you visit through links on our platform.'
            ]
        },
        {
            id: 'your-rights',
            title: '6. Your Rights',
            paragraphs: [
                'Under applicable Indian data protection laws, including the Digital Personal Data Protection Act, 2023, you have the right to access the personal information we hold about you, request corrections to inaccurate or incomplete data, and withdraw your consent for data processing at any time.',
                'You have the right to request deletion of your personal data, subject to certain legal exceptions such as compliance with legal obligations, resolution of disputes, or enforcement of our agreements. You may also request a copy of your personal data in a structured, commonly used, and machine-readable format.',
                'To exercise any of these rights, please contact our data protection team at the email address provided below. We will respond to your request within 30 days. Please note that certain requests may require us to verify your identity before processing.'
            ]
        },
        {
            id: 'data-retention',
            title: '7. Data Retention',
            paragraphs: [
                'We retain your personal information for as long as your account is active or as needed to provide you services, fulfil orders, and comply with our legal obligations. Order-related data, including transaction records and invoices, is retained for a minimum of 8 years as required under the Indian Income Tax Act and GST regulations.',
                'If you request deletion of your account, we will remove your personal profile data within 30 days. However, anonymised and aggregated data that does not identify you personally may be retained indefinitely for analytical and business purposes.',
                'Marketing communication preferences and consent records are maintained for the duration of your relationship with us and for a reasonable period thereafter to demonstrate compliance with applicable laws. Backup copies of data may persist in our systems for up to 90 days after deletion from primary storage.'
            ]
        },
        {
            id: 'contact-us',
            title: '8. Contact Us',
            paragraphs: [
                'If you have any questions, concerns, or requests regarding this Privacy Policy or our data handling practices, please contact our privacy team. We are committed to addressing your inquiries promptly and transparently.',
                'For privacy-related matters, you may reach us at privacy@yourbrand.com. We aim to respond to all privacy inquiries within 48 hours during business days. For urgent matters concerning data security, please include "URGENT" in your subject line.',
                'This Privacy Policy may be updated periodically to reflect changes in our practices, technology, or legal requirements. We will notify you of material changes by posting the updated policy on this page and, where appropriate, by sending you an email notification. The "Last Updated" date at the top of this page indicates when the policy was last revised.'
            ]
        }
    ];

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500;600;700&display=swap');
            `}</style>
            <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif" }} className="min-h-screen bg-[#0A0A0A]">
                <Header />

                <main>
                    {/* Hero Section */}
                    <section className="py-20 lg:py-28 px-4 lg:px-12 border-b border-[#2A2A2A]">
                        <div className="max-w-3xl mx-auto text-center">
                            <div className="w-16 h-16 border border-[#C9A96E]/30 rounded-full flex items-center justify-center mx-auto mb-8">
                                <Shield className="w-7 h-7 text-[#C9A96E]" />
                            </div>
                            <h1
                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                className="text-4xl lg:text-6xl font-light text-[#C9A96E] mb-6 tracking-wide"
                            >
                                Privacy Policy
                            </h1>
                            <p className="text-[#8A8A7A] text-lg max-w-2xl mx-auto leading-relaxed">
                                Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
                            </p>
                            <p className="text-[#8A8A7A]/60 text-sm mt-4 tracking-wide">
                                Last Updated: March 2026
                            </p>
                        </div>
                    </section>

                    {/* Table of Contents */}
                    <section className="py-12 lg:py-16 px-4 lg:px-12 border-b border-[#2A2A2A]">
                        <div className="max-w-3xl mx-auto">
                            <h2
                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                className="text-2xl font-light text-[#F5F0EB] mb-8 text-center"
                            >
                                Table of Contents
                            </h2>
                            <ol className="space-y-3">
                                {sections.map((section, index) => (
                                    <li key={index}>
                                        <button
                                            onClick={() => scrollToSection(section.id)}
                                            className="text-[#C9A96E] hover:text-[#D4B87A] transition-colors text-base lg:text-lg tracking-wide cursor-pointer text-left"
                                        >
                                            {section.title}
                                        </button>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </section>

                    {/* Sections */}
                    <section className="py-16 lg:py-24 px-4 lg:px-12">
                        <div className="max-w-3xl mx-auto">
                            <div className="space-y-16 lg:space-y-20">
                                {sections.map((section, index) => (
                                    <div key={index} id={section.id} className="scroll-mt-24">
                                        <h2
                                            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                            className="text-2xl lg:text-3xl font-light text-[#C9A96E] mb-8 tracking-wide"
                                        >
                                            {section.title}
                                        </h2>
                                        <div className="space-y-5">
                                            {section.paragraphs.map((paragraph, pIndex) => (
                                                <p
                                                    key={pIndex}
                                                    className="text-[#F5F0EB]/85 text-base leading-[1.7]"
                                                >
                                                    {paragraph}
                                                </p>
                                            ))}
                                        </div>
                                        {index < sections.length - 1 && (
                                            <div className="mt-16 lg:mt-20 border-b border-[#2A2A2A]" />
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Contact Section */}
                            <div className="mt-20 p-8 lg:p-10 bg-[#141414] border border-[#2A2A2A] rounded-lg">
                                <h3
                                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                    className="text-2xl font-light text-[#F5F0EB] mb-4"
                                >
                                    Questions About Your Privacy?
                                </h3>
                                <p className="text-[#8A8A7A] mb-6 leading-relaxed">
                                    If you have any questions about this Privacy Policy or our data practices, please contact us.
                                </p>
                                <p className="text-[#C9A96E] font-medium">
                                    Email: privacy@yourbrand.com
                                </p>
                            </div>
                        </div>
                    </section>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default PrivacyPolicy21;
